// The Orders screen's server state (spec/13 §5, F-POS-15), as TanStack Query hooks over the generated
// client — `packages/contracts/src/routes/commerce.ts`:
//
//   GET  …/sales                                  the venue's sales, newest first   orders:read
//   GET  …/sales/{id}                             one sale, with everything          orders:read
//   POST …/sales/{id}/payments                    take a tender (F-POS-12)           orders:create
//   POST …/sales/{id}/cancel                      cancel an unpaid sale              orders:void
//   POST …/sales/{id}/discount-code               apply a code (F-POS-02)            orders:create
//   POST …/sales/{id}/refunds                     refund a sale (F-POS-08)           orders:refund
//   POST …/sales/{id}/refunds/{rid}/approve       approve a pending refund           orders:refund_approve
//   GET  …/sales/{id}/document                     the receipt (F-POS-13)             orders:read
//   POST …/sales/{id}/document/send               send the receipt                   orders:read
//   POST …/sales/{id}/receipt                     print (or reprint) the receipt     orders:read
//
// The server computes every amount and decides every state; the console only reads and acts.

import type { ReceiptPrint, RefundSaleRequest, Sale, SaleDocument, SendSaleDocumentRequest, TakeSalePaymentRequest } from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';

export const orderKeys = {
  list: (organizationId: string, venueId: string) => ['orders', organizationId, venueId, 'list'] as const,
  /** The pending kiosk/app orders at the top of Sell (F-POS-10): placed, nothing paid. */
  awaiting: (organizationId: string, venueId: string) => ['orders', organizationId, venueId, 'awaiting'] as const,
  one: (organizationId: string, venueId: string, saleId: string) => ['orders', organizationId, venueId, 'one', saleId] as const,
  document: (organizationId: string, venueId: string, saleId: string) => ['orders', organizationId, venueId, 'document', saleId] as const,
};

/**
 * How often the list re-reads itself while the orders channel is not live (reduced motion, or disconnected): a kiosk
 * order placed at a PC still appears within this, and a live socket makes it immediate (`realtime.ts`).
 */
export const ORDERS_REFRESH_MS = 20_000;

/**
 * The venue's sales, newest first (F-POS-15). `live` is whether the orders channel is pushing (`useOrdersLive`);
 * without it the list polls every `ORDERS_REFRESH_MS`.
 */
export function useSales(organizationId: string, venueId: string, enabled: boolean, live = false) {
  const api = useApi();
  return useQuery<readonly Sale[]>({
    queryKey: orderKeys.list(organizationId, venueId),
    queryFn: async () => (await api.listSales({ path: { organizationId, venueId } })).sales,
    enabled,
    refetchInterval: live ? false : ORDERS_REFRESH_MS,
  });
}

/**
 * The venue's pending kiosk and app orders (F-POS-10, spec/13 §4): "Pay at the counter" orders that are placed and
 * have taken no payment, oldest first — the queue at the top of Sell. Live over `venue:{v}:orders` (`realtime.ts`);
 * without the push it polls like the Orders list.
 */
export function useAwaitingPayment(organizationId: string, venueId: string, enabled: boolean, live = false) {
  const api = useApi();
  return useQuery<readonly Sale[]>({
    queryKey: orderKeys.awaiting(organizationId, venueId),
    queryFn: async () => (await api.listSales({ path: { organizationId, venueId }, query: { awaiting: 'payment' } })).sales,
    enabled,
    refetchInterval: live ? false : ORDERS_REFRESH_MS,
  });
}

/** Reject a pending order from the queue (F-POS-10 rule 5): cancel it, allowed only before any payment settled. */
export function useRejectOrder(organizationId: string, venueId: string) {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (saleId: string) => api.cancelSale({ path: { organizationId, venueId, saleId } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: orderKeys.awaiting(organizationId, venueId) });
      void client.invalidateQueries({ queryKey: orderKeys.list(organizationId, venueId) });
    },
  });
}

/** One sale, with its lines, tenders, discounts, refunds and totals (F-POS-15). */
export function useSale(organizationId: string, venueId: string, saleId: string, enabled: boolean) {
  const api = useApi();
  return useQuery<Sale>({
    queryKey: orderKeys.one(organizationId, venueId, saleId),
    queryFn: () => api.getSale({ path: { organizationId, venueId, saleId } }),
    enabled: enabled && saleId !== '',
  });
}

/**
 * The sale's document, for print and to render on screen (F-POS-13). Absent (404) until the sale is paid — and for a
 * moment after: the document is numbered just after the payment commits (spec/06 §10.2), so while a paid sale has
 * none yet (`awaitingIssue`) it is looked for again every second.
 */
export function useSaleDocument(organizationId: string, venueId: string, saleId: string, enabled: boolean, awaitingIssue = false) {
  const api = useApi();
  return useQuery<SaleDocument | null>({
    queryKey: orderKeys.document(organizationId, venueId, saleId),
    queryFn: async () => {
      try {
        return await api.getSaleDocument({ path: { organizationId, venueId, saleId } });
      } catch {
        return null;
      }
    },
    enabled: enabled && saleId !== '',
    retry: false,
    // While a receipt is on its way, look again every two seconds until the provider has answered.
    refetchInterval: (query) => {
      if (query.state.data === null && awaitingIssue) return 1000;
      return query.state.data?.delivery?.status === 'queued' ? 2000 : false;
    },
  });
}

/** The mutations that change a sale re-read it and the list, so the drawer stays live. */
function useInvalidate(organizationId: string, venueId: string, saleId: string) {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: orderKeys.list(organizationId, venueId) });
    void client.invalidateQueries({ queryKey: orderKeys.awaiting(organizationId, venueId) });
    void client.invalidateQueries({ queryKey: orderKeys.one(organizationId, venueId, saleId) });
    void client.invalidateQueries({ queryKey: orderKeys.document(organizationId, venueId, saleId) });
  };
}

/** Take a tender against a sale (F-POS-12). */
export function useTakeSalePayment(organizationId: string, venueId: string, saleId: string) {
  const api = useApi();
  const invalidate = useInvalidate(organizationId, venueId, saleId);
  return useMutation({
    mutationFn: (body: TakeSalePaymentRequest) =>
      api.takeSalePayment({ path: { organizationId, venueId, saleId }, headerParams: { 'idempotency-key': crypto.randomUUID() }, body }),
    onSuccess: invalidate,
  });
}

/** Cancel an unpaid sale (F-POS-15). */
export function useCancelSale(organizationId: string, venueId: string, saleId: string) {
  const api = useApi();
  const invalidate = useInvalidate(organizationId, venueId, saleId);
  return useMutation({
    mutationFn: () => api.cancelSale({ path: { organizationId, venueId, saleId } }),
    onSuccess: invalidate,
  });
}

/** Apply a discount code at checkout (F-POS-02). */
export function useApplyDiscountCode(organizationId: string, venueId: string, saleId: string) {
  const api = useApi();
  const invalidate = useInvalidate(organizationId, venueId, saleId);
  return useMutation({
    mutationFn: (code: string) => api.applyDiscountCode({ path: { organizationId, venueId, saleId }, body: { code } }),
    onSuccess: invalidate,
  });
}

/**
 * Refund a sale in whole or in part (F-POS-08) — to a counter tender, or to the player's wallet (2026-10-02). A refund
 * to the wallet moves the player's balance, so the wallet reads are refreshed with the sale.
 */
export function useRefundSale(organizationId: string, venueId: string, saleId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const invalidate = useInvalidate(organizationId, venueId, saleId);
  return useMutation({
    mutationFn: (body: RefundSaleRequest) => api.refundSale({ path: { organizationId, venueId, saleId }, body }),
    onSuccess: (_sale, body) => {
      invalidate();
      if (body.tender === 'wallet') void queryClient.invalidateQueries({ queryKey: ['wallet', organizationId] });
    },
  });
}

/** Approve a pending refund — a second member (F-POS-08). It may settle to the wallet, so the wallet reads refresh too. */
export function useApproveRefund(organizationId: string, venueId: string, saleId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  const invalidate = useInvalidate(organizationId, venueId, saleId);
  return useMutation({
    mutationFn: (refundId: string) => api.approveSaleRefund({ path: { organizationId, venueId, saleId, refundId } }),
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['wallet', organizationId] });
    },
  });
}

/** Send the sale's receipt (F-POS-13). */
export function useSendSaleDocument(organizationId: string, venueId: string, saleId: string) {
  const api = useApi();
  const invalidate = useInvalidate(organizationId, venueId, saleId);
  return useMutation({
    mutationFn: (body: SendSaleDocumentRequest) => api.sendSaleDocument({ path: { organizationId, venueId, saleId }, body }),
    onSuccess: invalidate,
  });
}

/**
 * Print (or reprint) the sale's receipt (F-OPS-08): the server routes it to the receipt printer, its fallback, or
 * the browser when there is none, and answers the job and where it went. Every click is a new job.
 */
export function usePrintSaleReceipt(organizationId: string, venueId: string, saleId: string) {
  const api = useApi();
  return useMutation<ReceiptPrint, unknown, void>({
    mutationFn: () => api.printSaleReceipt({ path: { organizationId, venueId, saleId } }),
  });
}

/**
 * Open the sale's printable document in a new tab (F-CMP-07, spec/06 §10.2): the server's 80 mm receipt or A4 tax
 * invoice, whose print dialog prints it or saves it as a PDF. Read with the member's own session, then shown from a
 * local blob, so the tab needs no credentials of its own. The page carries no script.
 */
export function useOpenSaleDocument(organizationId: string, venueId: string, saleId: string) {
  const api = useApi();
  return useMutation({
    mutationFn: async (layout: 'receipt' | 'a4') => {
      // Opened first, on the click, so a pop-up blocker lets it through; filled once the page arrives.
      const tab = window.open('', '_blank');
      try {
        const html = await api.printSaleDocument({
          path: { organizationId, venueId, saleId },
          query: { layout },
          headers: { accept: 'text/html, application/problem+json' },
        });
        const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
        if (tab === null) window.location.assign(url);
        else tab.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (error) {
        tab?.close();
        throw error;
      }
    },
  });
}
